import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getCommentsByBook, type GetCommentsByBookParams, type CommentsPaginatedResponse, type Comment } from '../api/comments';
import { useMemo } from 'react';

interface UseCommentsByBookOptions {
  onSuccess?: (data: CommentsPaginatedResponse) => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
  page?: number;
  limit?: number;
}

export const useCommentsByBook = (
  bookId: number | null,
  options: UseCommentsByBookOptions = {}
) => {
  const { page = 1, limit = 10, enabled, ...queryOptions } = options;
  
  return useQuery<CommentsPaginatedResponse, Error>({ 
    queryKey: ['comments', bookId, page, limit],
    queryFn: () => getCommentsByBook(bookId!, { page, limit }),
    enabled: !!bookId && (enabled !== false),
    ...queryOptions,
  });
};

interface UseCommentsByBookInfiniteOptions {
  enabled?: boolean;
  limit?: number;
}

export const useCommentsByBookInfinite = (
  bookId: number | null,
  options: UseCommentsByBookInfiniteOptions = {}
) => {
  const { enabled = true, limit = 10 } = options;

  const query = useInfiniteQuery({
    queryKey: ['commentsInfinite', bookId, limit],
    queryFn: async ({ pageParam = 1 }) => {
      return getCommentsByBook(bookId!, { page: pageParam, limit });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_next) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    enabled: !!bookId && enabled,
    staleTime: 30_000,
  });

  const comments = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.comments || []);
  }, [query.data?.pages]);

  const totalCount = query.data?.pages[0]?.pagination?.total_count ?? 0;

  return {
    comments,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
  };
};
